/* Game of Nuts theme: product page behaviour. No dependencies. */
(() => {
  'use strict';
  if (window.__gonProductJs) return;
  window.__gonProductJs = true;

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ================= Product section ================= */
  class ProductSection {
    constructor(root) {
      this.root = root;
      const variantsEl = qs('[data-variants]', root);
      const labelsEl = qs('[data-product-labels]', root);
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
      this.labels = labelsEl ? JSON.parse(labelsEl.textContent) : { add: 'Add to cart', soldOut: 'Sold out', unavailable: 'Unavailable' };
      this.picker = qs('[data-variant-picker]', root);
      this.idInput = qs('[data-variant-id]', root);
      this.addBtn = qs('[data-add-btn]', root);
      this.addLabel = qs('[data-add-label]', root);
      this.priceCur = qs('[data-price-current]', root);
      this.priceCmp = qs('[data-price-compare]', root);
      this.priceSave = qs('[data-price-save]', root);

      this.initGallery();
      this.initPicker();
      this.initSticky();
      this.initExtras();
      if (this.addBtn && this.addBtn.disabled) this.addBtn.dataset.soldOut = 'true';
    }

    /* ---------- Gallery ---------- */
    initGallery() {
      const g = qs('[data-gallery]', this.root);
      this.track = g && qs('[data-gallery-track]', g);
      if (!this.track) return;
      this.items = qsa('.gallery__item', this.track);
      this.thumbs = qsa('[data-thumb]', g);
      const dotsBox = qs('[data-gallery-dots]', g);
      this.dots = [];
      if (dotsBox && this.items.length > 1) {
        this.items.forEach((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'slider__dot slider__dot--dark';
          b.setAttribute('aria-label', `${i + 1} / ${this.items.length}`);
          b.addEventListener('click', () => this.goTo(i));
          dotsBox.appendChild(b);
          this.dots.push(b);
        });
      }
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(parseInt(t.dataset.thumb, 10))));
      if (this.items.length > 1 && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting && en.intersectionRatio >= 0.6) this.markMedia(this.items.indexOf(en.target)); });
        }, { root: this.track, threshold: [0.6] });
        this.items.forEach((it) => io.observe(it));
      }
      this.markMedia(0);
    }
    goTo(i) {
      const el = this.items[i];
      if (!el) return;
      this.track.scrollTo({ left: el.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    markMedia(i) {
      this.thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
    }

    /* ---------- Variant picker ---------- */
    initPicker() {
      if (!this.picker) return;
      this.picker.addEventListener('change', () => this.onOptionChange());
      this.refreshAvailability();
    }
    selectedOptions() {
      return qsa('.option', this.picker).map((fs) => {
        const checked = qs('input:checked', fs);
        return checked ? checked.value : null;
      });
    }
    findVariant(opts) {
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    onOptionChange() {
      const opts = this.selectedOptions();
      qsa('.option', this.picker).forEach((fs, i) => {
        const label = qs('[data-option-selected]', fs);
        if (label && opts[i]) label.textContent = opts[i];
      });
      const variant = this.findVariant(opts);
      this.refreshAvailability();
      this.render(variant);
    }
    refreshAvailability() {
      const opts = this.selectedOptions();
      qsa('.option', this.picker).forEach((fs, i) => {
        qsa('.option__input', fs).forEach((input) => {
          const test = opts.slice();
          test[i] = input.value;
          const v = this.findVariant(test);
          const label = qs(`label[for="${input.id}"]`, fs);
          if (label) {
            label.classList.toggle('is-soldout', !!v && !v.available);
            label.classList.toggle('is-unavailable', !v);
          }
        });
      });
    }
    render(variant) {
      const sticky = qs('[data-sticky-atc]', this.root);
      const stickyBtn = qs('[data-sticky-btn]', this.root);
      const stickyLabel = qs('[data-sticky-label]', this.root);
      const stickyPrice = qs('[data-sticky-price]', this.root);

      if (!variant) {
        if (this.addBtn) { this.addBtn.disabled = true; this.addBtn.dataset.soldOut = 'true'; }
        if (this.addLabel) this.addLabel.textContent = this.labels.unavailable;
        if (stickyBtn) stickyBtn.disabled = true;
        if (stickyLabel) stickyLabel.textContent = this.labels.unavailable;
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;

      if (this.priceCur) {
        this.priceCur.textContent = variant.price;
        this.priceCur.classList.toggle('is-sale', !!variant.compare);
      }
      if (this.priceCmp) { this.priceCmp.hidden = !variant.compare; if (variant.compare) this.priceCmp.textContent = variant.compare; }
      if (this.priceSave) { this.priceSave.hidden = !variant.compare; if (variant.compare) this.priceSave.textContent = variant.save; }
      if (stickyPrice) stickyPrice.textContent = variant.price;

      const label = variant.available ? this.labels.add : this.labels.soldOut;
      if (this.addBtn) { this.addBtn.disabled = !variant.available; this.addBtn.dataset.soldOut = String(!variant.available); }
      if (this.addLabel) this.addLabel.textContent = label;
      if (stickyBtn) stickyBtn.disabled = !variant.available;
      if (stickyLabel) stickyLabel.textContent = label;

      // keep the address bar shareable
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // show the variant's own image
      if (variant.media && this.items) {
        const idx = this.items.findIndex((el) => String(el.dataset.mediaId) === String(variant.media));
        if (idx > -1) this.goTo(idx);
      }
    }

    /* ---------- Sticky add-to-cart bar ---------- */
    initSticky() {
      const bar = qs('[data-sticky-atc]', this.root);
      const target = this.addBtn;
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      const btn = qs('[data-sticky-btn]', this.root);
      if (btn) btn.addEventListener('click', () => {
        const form = target.closest('form');
        if (form && form.requestSubmit) form.requestSubmit(target);
        else target.click();
      });
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const past = !en.isIntersecting && en.boundingClientRect.top < 0;
          bar.hidden = !past;
          document.body.classList.toggle('has-sticky-atc', past);
        });
      });
      io.observe(target);
    }

    /* ---------- Copy coupon code + share ---------- */
    initExtras() {
      qsa('[data-copy]', this.root).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const code = btn.dataset.copy;
          try { await navigator.clipboard.writeText(code); }
          catch (e) {
            const ta = document.createElement('textarea');
            ta.value = code; document.body.appendChild(ta); ta.select();
            try { document.execCommand('copy'); } catch (err) { /* ignore */ }
            ta.remove();
          }
          const tag = qs('.offer__copy', btn);
          if (tag) { const old = tag.textContent; tag.textContent = btn.dataset.copied || 'Copied'; setTimeout(() => { tag.textContent = old; }, 1800); }
        });
      });
      qsa('[data-share]', this.root).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const data = { title: btn.dataset.title, url: btn.dataset.url };
          if (navigator.share) { try { await navigator.share(data); } catch (e) { /* cancelled */ } return; }
          try {
            await navigator.clipboard.writeText(data.url);
            const old = btn.textContent;
            btn.textContent = (window.GON && GON.strings && GON.strings.copied) || 'Link copied';
            setTimeout(() => { btn.textContent = old; }, 1800);
          } catch (e) { /* ignore */ }
        });
      });
    }
  }

  /* ================= Recommendations (loaded after page paint) ================= */
  const loadRecommendations = (el) => {
    if (el.dataset.loaded) return;
    el.dataset.loaded = 'true';
    fetch(el.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = qs('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
        else el.remove();
      })
      .catch(() => el.remove());
  };

  const init = (scope = document) => {
    qsa('[data-product-section]', scope).forEach((el) => { if (!el.__product) el.__product = new ProductSection(el); });
    qsa('[data-recommendations]', scope).forEach((el) => {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          if (entries.some((e) => e.isIntersecting)) { loadRecommendations(el); obs.disconnect(); }
        }, { rootMargin: '400px' });
        io.observe(el);
      } else loadRecommendations(el);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
